import { FC } from 'react';
import {
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import Meta from '@bizapp-lp/workflow/organisms/Meta';
import { Terms } from '@bizapp-lp/workflow/templates/Terms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    innerList: {
      '& > li': {
        listStyle: 'none',
        marginLeft: theme.spacing(-4),
      },
    },
    noteList: {
      margin: '8px 0 0 !important',
      padding: '0 !important',
      '& li': {
        position: 'static',
        listStyle: 'none',
        marginTop: '-13px !important',
        marginBottom: '0 !important',
        '&:before': {
          content: "'※'",
          position: 'relative',
          top: '18px',
          left: '-14px',
          fontSize: '14px',
          lineSpacing: '21px',
        },
      },
    },
  }),
);

const title =
  'Workflowトライアルサービス仕様書 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、Workflowトライアルサービス仕様書です。';

const TrialSpec: FC = () => {
  const classes = useStyles();

  const termsProps = {
    pageTitle: (
      <>
        <span style={{ display: 'block' }}>HUE Works Suite</span>
        <span style={{ display: 'block' }}>
          Workflowトライアルサービス仕様書
        </span>
      </>
    ),
    headerContent: 'Ver.1.0.0',
  };
  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <Terms {...termsProps}>
        <Container disableGutters maxWidth={false}>
          <Typography>
            これは過去のHUE Works Suite
            Workflowトライアルサービス仕様書です。最新版は
            <Link href="https://www.worksap.co.jp/terms/saas/workflow/trial/latest.html">
              こちら
            </Link>
            です。
          </Typography>
          <Typography variant="h2">1. 本書の定義</Typography>
          <Typography variant="body1">
            本書は、お客様におけるHUE Works Suite Workflow（以下、
            Workflow）トライアル利用にあたり株式会社ワークスアプリケーションズ・エンタープライズ（以下、弊社）より提供可能なクラウドサービスの内容を記述したものです。Workflowのトライアル利用に際しては、本内容および「HUE
            Works Suite / HUE Works Suite DX
            Solutions利用規約」に同意していただく必要があります。
          </Typography>
          <Typography variant="body1">
            本書と「HUE Works Suite / HUE Works Suite DX
            Solutions利用規約」の記載内容が異なる場合には利用規約の内容が優先されます。
          </Typography>
          <Typography variant="body1">
            過去に提示済みのサービス内容と内容が異なる場合には、常に最新のサービス内容が優先して適用されます。
          </Typography>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">
            2. Workflowトライアルサービス概要
          </Typography>
          <Typography variant="body1">
            Workflowは法人向けのワークフローサービスです。ノンプログラミングで簡単に申請経路を作成することができ、ペーパーレス化や業務効率の向上を支援します。
          </Typography>
          <Typography variant="body1">
            トライアル利用では、トライアルサービス利用開始日から30日間、最大20ライセンス、利用可能容量60GBまでの範囲でサービスを無料でご利用いただけます。
          </Typography>
          <Typography variant="body1">
            ※本サービスは法人のお客様向けサービスです。ご利用は法人（公的機関を含む）に限らせていただきます。
          </Typography>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.1 標準サービス機能</Typography>
            <Typography variant="body1">
              Workflow(トライアル版)では下記サービス機能を提供いたします。あわせて、初期構築作業（利用環境構築作業および管理者アカウント作成作業）を提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記機能および初期構築作業の仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>管理機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>サービス管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>処理フロー管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>トレイ管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>報告管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>発番管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>印影設定</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>データパイプライン管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ロール管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>メール設定</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>権限管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>IPアドレス制限</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
              <ListItem>
                <ListItemText>ユーザー機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>申請</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>承認・差し戻し・却下・保留</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      書類情報表示（書類を一時保存、未処理、処理済みなどのステータスごとに表示）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>報告（申請内容を集計して報告）</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      データパイプライン（CSVファイル取り込みによりマスターを作成）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      通知（承認依頼時や承認完了時にベル通知とメール通知を送信）
                    </ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            </List>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.2 環境構築およびアカウントの発行
            </Typography>
            <Typography variant="body1">
              お申し込みから1週間ほどでお申し込み時に登録いただいたメールアドレスあてに、アクセスURLと管理者アカウントの初期ログインIDをお送りいたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.3 サポートサービス</Typography>
            <Typography variant="body1">
              お申し込みいただきましたメールアドレスあてに機能マニュアル、管理者マニュアルおよび問い合わせ先メールアドレスをお送りいたします。ご不明点がありましたら問い合わせ先メールアドレスまでお問い合わせください。3営業日をめどに１次回答いたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.4 提供条件</Typography>
            <Typography variant="body1">
              Workflowの提供に際しては、GrapeCity,Inc.（以下「GC」といいます。）が提供するSpreadJSが利用されます。
              お客様は、Workflowを利用するにあたり、GC所定の「End-User License
              Agreement For GrapeCity SpreadJS
              Software」（その名称又は内容に変更が生じた場合には、当該変更後の名称及び内容によるものとします。
              なお、最新版は{' '}
              <Link href="https://www.grapecity.com/legal/eula">
                https://www.grapecity.com/legal/eula
              </Link>{' '}
              に掲載されています。）のうちSpreadJSを利用して開発されたソフトウェアのユーザーが遵守すべき事項について同意し、これを遵守するものとします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.5 トライアル期間の終了</Typography>
            <Typography variant="body1">
              無料トライアル期間は、以下いずれか早く到来する日で終了します。
            </Typography>
            <List component="ol">
              <ListItem>
                <ListItemText>
                  トライアルサービス利用開始日から30日後の日
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>有償サービスの開始日</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>
                  お客様からトライアル利用解約の申込をいただいてから3日後または解約を希望する日のいずれか遅い日
                </ListItemText>
              </ListItem>
            </List>
            <Typography variant="body1">
              トライアル期間終了後、自動的に課金されることはありません。有償サービスの利用を希望される場合は、弊社指定のフォームで有償サービス契約をお申込みください。
            </Typography>
          </Container>
        </Container>
      </Terms>
    </>
  );
};

export default TrialSpec;
